import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import { cn } from '@/lib/utils';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import {
  Play, Square, Clock, Calendar, CalendarDays, CalendarRange,
  Save, AlertTriangle, Check, X, ChevronLeft, ChevronRight, Copy, FileDown
} from 'lucide-react';
import { ChildIcon, genderBg, genderColor, genderSelectedBg, genderOutlineBg } from '../components/ChildIcon';
import { toast } from 'sonner';
import { WeekView } from '../components/time/WeekView';
import { MonthView } from '../components/time/MonthView';
import { EntryHistory } from '../components/time/EntryHistory';
import AbsenceDialog from '../components/AbsenceDialog';
import { getAbsenceLabel, DAYS_SHORT_3 as DAYS_SHORT, API_BASE as API } from '../utils/constants';

const DAYS_FULL = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
const DAY_IDS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

// Extract error message from axios error (handles Pydantic validation arrays)
function getErrorMessage(error, fallback = 'Erreur') {
  const detail = error?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map(d => d.msg || d.message || JSON.stringify(d)).join(', ');
  }
  return fallback;
}

// ========== HELPERS ==========
// Timezone-safe: always build date strings manually to avoid UTC shift via toISOString()
function toLocalDateStr(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function getTodayStr() {
  return toLocalDateStr(new Date());
}

// Parse a "YYYY-MM-DD" string as local (noon to avoid any DST edge case)
function parseLocalDate(dateStr) {
  return new Date(dateStr + 'T12:00:00');
}

// ========== JOURS FÉRIÉS FRANÇAIS ==========
// Calcul de Pâques (algorithme de Meeus/Jones/Butcher)
function computeEaster(year) {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
}

function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

// ── Holiday data from backend (replaces hardcoded getFrenchHolidays) ──
// Kept for fallback only
function getFrenchHolidaysFallback(year) {
  const easter = computeEaster(year);
  const holidays = new Map();
  const fmt = (d) => toLocalDateStr(d);
  holidays.set(`${year}-01-01`, "Jour de l'An");
  holidays.set(`${year}-05-01`, "Fête du Travail");
  holidays.set(`${year}-05-08`, "Victoire 1945");
  holidays.set(`${year}-07-14`, "Fête Nationale");
  holidays.set(`${year}-08-15`, "Assomption");
  holidays.set(`${year}-11-01`, "Toussaint");
  holidays.set(`${year}-11-11`, "Armistice 1918");
  holidays.set(`${year}-12-25`, "Noël");
  holidays.set(fmt(addDays(easter, 1)), "Lundi de Pâques");
  holidays.set(fmt(addDays(easter, 39)), "Ascension");
  holidays.set(fmt(addDays(easter, 50)), "Lundi de Pentecôte");
  return holidays;
}

// Fallback cache
const _holidayFallbackCache = {};
function getHolidayNameFallback(dateStr) {
  const year = parseInt(dateStr.substring(0, 4), 10);
  if (!_holidayFallbackCache[year]) {
    _holidayFallbackCache[year] = getFrenchHolidaysFallback(year);
  }
  return _holidayFallbackCache[year].get(dateStr) || null;
}

function getWeekDates(refDate) {
  const d = new Date(refDate);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  const monday = new Date(d.setDate(diff));
  const dates = [];
  for (let i = 0; i < 7; i++) {
    const dt = new Date(monday);
    dt.setDate(monday.getDate() + i);
    dates.push(toLocalDateStr(dt));
  }
  return dates;
}

function getMonthDates(yearMonth) {
  const [y, m] = yearMonth.split('-').map(Number);
  const daysInMonth = new Date(y, m, 0).getDate();
  const dates = [];
  for (let d = 1; d <= daysInMonth; d++) {
    dates.push(`${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`);
  }
  return dates;
}

function formatDateFr(dateStr) {
  return parseLocalDate(dateStr).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
}

function formatWeekLabel(dates) {
  if (!dates.length) return '';
  const s = parseLocalDate(dates[0]).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  const e = parseLocalDate(dates[6]).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
  return `${s} - ${e}`;
}

// ========== MAIN COMPONENT ==========
const TimeTracking = ({ embedded = false }) => {
  const { user } = useAuth();
  const [contracts, setContracts] = useState([]);
  const [selectedContract, setSelectedContract] = useState(null);
  const [entries, setEntries] = useState([]);
  const [activeEntries, setActiveEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [absenceOpen, setAbsenceOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState('day'); // day, week, month

  // Week navigation
  const [weekRef, setWeekRef] = useState(new Date());
  const weekDates = getWeekDates(weekRef);
  const [weekData, setWeekData] = useState({}); // { date: { start: '', end: '', hours: '' } }

  // Month navigation
  const now = new Date();
  const currentYearMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const [monthRef, setMonthRef] = useState(currentYearMonth);
  const monthDates = getMonthDates(monthRef);
  const [monthData, setMonthData] = useState({}); // { date: { hours: '' } }

  const currentMonth = currentYearMonth;

  // Stable refs for values used in callbacks (prevents stale closures without triggering re-fetches)
  const selectedContractRef = useRef(selectedContract);
  selectedContractRef.current = selectedContract;

  // ── Backend-driven holiday map (per contract) ──
  const [holidayMap, setHolidayMap] = useState(new Map());

  // ── Approved leave requests (congés) — displayed on calendar ──
  const [leaveMap, setLeaveMap] = useState(new Map()); // date_str → { type: "Congé payé", status: "approved" }

  useEffect(() => {
    if (!selectedContract) return;
    let cancelled = false;
    const year = new Date().getFullYear();
    (async () => {
      try {
        const res = await axios.get(`${API}/holidays/${year}/contract/${selectedContract}`);
        if (cancelled) return;
        const map = new Map();
        for (const h of (res.data.holidays || [])) {
          map.set(h.date, h.name);
        }
        setHolidayMap(map);
      } catch (err) {
        // Fallback to local computation
        console.warn('[TimeTracking] holidays fetch failed, using fallback:', err.message || err);
        if (!cancelled) {
          const fallback = getFrenchHolidaysFallback(year);
          setHolidayMap(fallback);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [selectedContract]);

  // Fetch approved leave requests for the selected contract
  useEffect(() => {
    if (!selectedContract) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/leaves`, { params: { contract_id: selectedContract } });
        if (cancelled) return;
        const map = new Map();
        for (const leave of (res.data || [])) {
          if (leave.status !== 'approved' && leave.status !== 'pending') continue;
          // Expand date range into individual dates
          const start = new Date(leave.start_date + 'T12:00:00');
          const end = new Date(leave.end_date + 'T12:00:00');
          for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
            const dow = d.getDay();
            if (dow === 0) continue; // Skip Sundays
            const dateStr = toLocalDateStr(d);
            map.set(dateStr, {
              type: 'Congé payé',
              status: leave.status,
            });
          }
        }
        setLeaveMap(map);
      } catch (err) {
        console.warn('[TimeTracking] leaves fetch failed:', err.message || err);
        if (!cancelled) setLeaveMap(new Map());
      }
    })();
    return () => { cancelled = true; };
  }, [selectedContract]);

  // getHolidayName uses the backend-driven map (or fallback)
  const getHolidayName = useCallback((dateStr) => {
    if (holidayMap.size > 0) {
      return holidayMap.get(dateStr) || null;
    }
    return getHolidayNameFallback(dateStr);
  }, [holidayMap]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [contractsRes, activeRes, entriesRes] = await Promise.all([
        axios.get(`${API}/contracts`),
        axios.get(`${API}/time/active`),
        axios.get(`${API}/time/entries?month=${currentMonth}`)
      ]);
      setContracts(contractsRes.data);
      setActiveEntries(activeRes.data);
      setEntries(entriesRes.data);
      if (contractsRes.data.length > 0 && !selectedContractRef.current) {
        setSelectedContract(contractsRes.data[0].id);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [currentMonth]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Prefill week/month data when entries or mode changes
  // Using a serialized key for weekDates to avoid object reference changes
  const weekDatesKey = weekDates.join(',');
  useEffect(() => {
    if (!selectedContract) return;
    const contractEntries = entries.filter(e => e.contract_id === selectedContract);

    if (mode === 'week') {
      const wd = {};
      weekDates.forEach(d => {
        const existing = contractEntries.find(e => e.date === d && !e.is_absent);
        const absEntry = contractEntries.find(e => e.date === d && e.is_absent);
        const leaveInfo = leaveMap.get(d);
        const isAbsent = !!absEntry || (!!leaveInfo && leaveInfo.status === 'approved');
        // Determine absence type label
        let absenceLabel = null;
        if (absEntry) {
          absenceLabel = getAbsenceLabel(absEntry.absence_type);
        } else if (leaveInfo && leaveInfo.status === 'approved') {
          absenceLabel = leaveInfo.type || 'Congé payé';
        } else if (leaveInfo && leaveInfo.status === 'pending') {
          absenceLabel = 'Congé (en attente)';
        }
        wd[d] = {
          start: existing?.start_time ? existing.start_time.substring(11, 16) : '',
          end: existing?.end_time ? existing.end_time.substring(11, 16) : '',
          hours: existing?.hours_worked ? String(existing.hours_worked) : '',
          has_entry: !!existing,
          is_absent: isAbsent,
          absence_type: absenceLabel,
          is_leave_pending: leaveInfo?.status === 'pending' && !absEntry,
        };
      });
      setWeekData(wd);
    }

    if (mode === 'month') {
      const md = {};
      monthDates.forEach(d => {
        const existing = contractEntries.find(e => e.date === d && !e.is_absent);
        const absEntry = contractEntries.find(e => e.date === d && e.is_absent);
        const leaveInfo = leaveMap.get(d);
        const isAbsent = !!absEntry || (!!leaveInfo && leaveInfo.status === 'approved');
        // Determine absence type label
        let absenceLabel = null;
        if (absEntry) {
          absenceLabel = getAbsenceLabel(absEntry.absence_type);
        } else if (leaveInfo && leaveInfo.status === 'approved') {
          absenceLabel = leaveInfo.type || 'Congé payé';
        } else if (leaveInfo && leaveInfo.status === 'pending') {
          absenceLabel = 'Congé (en attente)';
        }
        md[d] = {
          hours: existing?.hours_worked ? String(existing.hours_worked) : '',
          has_entry: !!existing,
          is_absent: isAbsent,
          absence_type: absenceLabel,
          is_leave_pending: leaveInfo?.status === 'pending' && !absEntry,
        };
      });
      setMonthData(md);
    }
  }, [mode, selectedContract, entries, weekDatesKey, monthRef, leaveMap]);

  // Reload entries when month changes
  useEffect(() => {
    if (mode === 'month' && selectedContract) {
      axios.get(`${API}/time/entries?month=${monthRef}&contract_id=${selectedContract}`)
        .then(res => {
          // Merge with existing entries
          setEntries(prev => {
            const otherEntries = prev.filter(e => e.contract_id !== selectedContract || !e.date?.startsWith(monthRef));
            return [...otherEntries, ...res.data];
          });
        })
        .catch(() => {});
    }
  }, [monthRef, selectedContract, mode]);

  const handleStart = async (contractId) => {
    try {
      await axios.post(`${API}/time/start`, { contract_id: contractId });
      toast.success('Pointage démarré');
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erreur'));
    }
  };

  const handleStop = async (entryId) => {
    try {
      const res = await axios.post(`${API}/time/stop/${entryId}`);
      toast.success(`Arrêté : ${res.data.hours_worked}h`);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erreur'));
    }
  };

  // Save week data
  const handleSaveWeek = async () => {
    if (!selectedContract) return;
    setSaving(true);
    try {
      const bulkEntries = [];
      Object.entries(weekData).forEach(([date, data]) => {
        if (data.hours || data.start) {
          bulkEntries.push({
            date,
            start_time: data.start || undefined,
            end_time: data.end || undefined,
            hours_worked: data.hours ? parseFloat(data.hours) : undefined,
          });
        }
      });
      if (bulkEntries.length === 0) {
        toast.info('Aucune heure saisie');
        setSaving(false);
        return;
      }
      const res = await axios.post(`${API}/time/bulk-entries`, {
        contract_id: selectedContract,
        entries: bulkEntries
      });
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erreur lors de la sauvegarde'));
    } finally {
      setSaving(false);
    }
  };

  // Save month data
  const handleSaveMonth = async () => {
    if (!selectedContract) return;
    setSaving(true);
    try {
      const bulkEntries = [];
      Object.entries(monthData).forEach(([date, data]) => {
        if (data.hours && parseFloat(data.hours) > 0) {
          bulkEntries.push({ date, hours_worked: parseFloat(data.hours) });
        }
      });
      if (bulkEntries.length === 0) {
        toast.info('Aucune heure saisie');
        setSaving(false);
        return;
      }
      const res = await axios.post(`${API}/time/bulk-entries`, {
        contract_id: selectedContract,
        entries: bulkEntries
      });
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erreur lors de la sauvegarde'));
    } finally {
      setSaving(false);
    }
  };

  const getContractName = (id) => contracts.find(c => c.id === id)?.child_name || '';
  const getActiveForContract = (contractId) => activeEntries.find(e => e.contract_id === contractId);
  const getSelectedContract = () => contracts.find(c => c.id === selectedContract);

  // Apply contract schedule to week grid (pre-fill from contract's working_days/daily_schedule)
  const applyContractSchedule = () => {
    const contract = getSelectedContract();
    if (!contract) return;

    const schedule = contract.daily_schedule || {};
    const workingDays = contract.working_days || [];
    const newWeekData = { ...weekData };

    weekDates.forEach((dateStr, idx) => {
      const dayId = DAY_IDS[idx];
      const data = newWeekData[dateStr] || {};

      // Skip if absent or already has an entry the user has modified
      if (data.is_absent) return;

      if (workingDays.includes(dayId)) {
        const daySchedule = schedule[dayId] || schedule[workingDays[0]] || { start: '08:00', end: '18:00' };
        const s = daySchedule.start || '08:00';
        const e = daySchedule.end || '18:00';
        const sParts = s.split(':').map(Number);
        const eParts = e.split(':').map(Number);
        const h = (eParts[0] * 60 + eParts[1] - sParts[0] * 60 - sParts[1]) / 60;

        newWeekData[dateStr] = {
          ...data,
          start: s,
          end: e,
          hours: h > 0 ? h.toFixed(1) : '',
        };
      } else {
        // Non-working day: leave empty
        newWeekData[dateStr] = { ...data, start: '', end: '', hours: '' };
      }
    });

    setWeekData(newWeekData);
    toast.success('Horaires du contrat appliqués');
  };

  // Apply contract schedule to month grid
  const applyContractScheduleMonth = () => {
    const contract = getSelectedContract();
    if (!contract) return;

    const schedule = contract.daily_schedule || {};
    const workingDays = contract.working_days || [];
    const newMonthData = { ...monthData };

    monthDates.forEach((dateStr) => {
      const dayOfWeek = parseLocalDate(dateStr).getDay();
      const dayId = DAY_IDS[dayOfWeek === 0 ? 6 : dayOfWeek - 1]; // JS Sunday=0 → our index 6
      const data = newMonthData[dateStr] || {};

      if (data.is_absent) return;

      if (workingDays.includes(dayId)) {
        const daySchedule = schedule[dayId] || schedule[workingDays[0]] || { start: '08:00', end: '18:00' };
        const s = daySchedule.start || '08:00';
        const e = daySchedule.end || '18:00';
        const sParts = s.split(':').map(Number);
        const eParts = e.split(':').map(Number);
        const h = (eParts[0] * 60 + eParts[1] - sParts[0] * 60 - sParts[1]) / 60;

        newMonthData[dateStr] = { ...data, hours: h > 0 ? h.toFixed(1) : '' };
      } else {
        newMonthData[dateStr] = { ...data, hours: '' };
      }
    });

    setMonthData(newMonthData);
    toast.success('Horaires du contrat appliqués au mois');
  };

  // Copy previous week data
  const copyPreviousWeek = async () => {
    if (!selectedContract) return;

    const prevWeekRef = new Date(weekRef);
    prevWeekRef.setDate(prevWeekRef.getDate() - 7);
    const prevDates = getWeekDates(prevWeekRef);

    // Determine month of previous week for the API query
    const prevMonths = [...new Set(prevDates.map(d => d.slice(0, 7)))];

    try {
      const allEntries = [];
      for (const m of prevMonths) {
        const res = await axios.get(`${API}/time/entries?month=${m}&contract_id=${selectedContract}`);
        allEntries.push(...res.data);
      }

      const newWeekData = { ...weekData };
      let copied = 0;

      weekDates.forEach((dateStr, idx) => {
        const prevDate = prevDates[idx];
        const prevEntry = allEntries.find(e => e.date === prevDate && !e.is_absent && e.contract_id === selectedContract);
        const currentData = newWeekData[dateStr] || {};

        if (currentData.is_absent) return;

        if (prevEntry) {
          newWeekData[dateStr] = {
            ...currentData,
            start: prevEntry.start_time ? prevEntry.start_time.substring(11, 16) : '',
            end: prevEntry.end_time ? prevEntry.end_time.substring(11, 16) : '',
            hours: prevEntry.hours_worked ? String(prevEntry.hours_worked) : '',
          };
          copied++;
        }
      });

      setWeekData(newWeekData);
      if (copied > 0) {
        toast.success(`${copied} jour(s) copiés depuis la semaine précédente`);
      } else {
        toast.info('Aucune donnée trouvée la semaine précédente');
      }
    } catch (error) {
      toast.error('Erreur lors de la copie');
    }
  };

  // Week totals
  const weekTotal = Object.values(weekData).reduce((sum, d) => sum + (parseFloat(d.hours) || 0), 0);
  // Month totals
  const monthTotal = Object.values(monthData).reduce((sum, d) => sum + (parseFloat(d.hours) || 0), 0);

  if (loading) {
    return (
      <div className={embedded ? "space-y-6" : "container-custom py-6 space-y-6"}>
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  return (
    <div className={embedded ? "space-y-6" : "container-custom py-6 space-y-6"} data-testid="time-tracking-page">
      {!embedded && (
        <div>
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'var(--font-secondary)' }}>
            Gestion du temps
          </h1>
          <p className="text-[var(--text-secondary)] mt-1">
            Suivez vos heures de travail quotidiennes
          </p>
        </div>
      )}

      {contracts.length === 0 ? (
        <Card className="border-dashed">
          <CardHeader className="text-center py-8">
            <CardTitle>Aucun contrat</CardTitle>
            <CardDescription>Créez ou rejoignez un contrat pour commencer le pointage.</CardDescription>
          </CardHeader>
        </Card>
      ) : (
        <>
          {/* Child selector — boutons avec icône genre */}
          {contracts.length > 1 ? (
            <div className="flex items-center gap-2 flex-wrap" data-testid="contract-selector">
              {contracts.map(c => {
                const isGirl = c.child_sex === 'F';
                const isSelected = selectedContract === c.id;
                const name = c.child_first_name || c.child_name;
                return (
                  <Button
                    key={c.id}
                    variant={isSelected ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSelectedContract(c.id)}
                    className={cn(
                      'gap-2 transition-all duration-150',
                      isSelected && genderSelectedBg(c.child_sex),
                      !isSelected && genderOutlineBg(c.child_sex),
                    )}
                    data-testid={`child-btn-${c.id}`}
                  >
                    <ChildIcon sex={c.child_sex} className="h-4 w-4" />
                    {name}
                  </Button>
                );
              })}
            </div>
          ) : contracts.length === 1 && (
            <div className="flex items-center gap-2">
              {(() => {
                const c = contracts[0];
                const name = c.child_first_name || c.child_name;
                return (
                  <Badge variant="outline" className={cn(
                    'text-sm py-1.5 px-3 gap-2',
                  )} style={{ borderColor: genderColor(c.child_sex), color: genderColor(c.child_sex), backgroundColor: genderBg(c.child_sex) }}>
                    <ChildIcon sex={c.child_sex} className="h-4 w-4" />
                    {name}
                  </Badge>
                );
              })()}
            </div>
          )}

          {/* Mode Tabs */}
          <Tabs value={mode} onValueChange={setMode} className="w-full">
            <TabsList className="w-full sm:w-auto h-auto p-1" style={{ backgroundColor: 'var(--neutral-100)' }}>
              <TabsTrigger value="day" className="gap-1.5 text-sm py-2 data-[state=active]:bg-[var(--bg-primary)] data-[state=active]:text-[var(--primary-400)] data-[state=active]:shadow-sm" data-testid="mode-day">
                <Calendar className="h-4 w-4" />
                Jour
              </TabsTrigger>
              <TabsTrigger value="week" className="gap-1.5 text-sm py-2 data-[state=active]:bg-[var(--bg-primary)] data-[state=active]:text-[var(--primary-400)] data-[state=active]:shadow-sm" data-testid="mode-week">
                <CalendarDays className="h-4 w-4" />
                Semaine
              </TabsTrigger>
              <TabsTrigger value="month" className="gap-1.5 text-sm py-2 data-[state=active]:bg-[var(--bg-primary)] data-[state=active]:text-[var(--primary-400)] data-[state=active]:shadow-sm" data-testid="mode-month">
                <CalendarRange className="h-4 w-4" />
                Mois
              </TabsTrigger>
            </TabsList>

            {/* ========== DAY MODE ========== */}
            <TabsContent value="day" className="mt-4 space-y-4">
              {contracts.filter(c => !selectedContract || c.id === selectedContract).map(contract => {
                const active = getActiveForContract(contract.id);
                const todayEntries = entries.filter(
                  e => e.contract_id === contract.id && e.date === getTodayStr()
                );

                return (
                  <Card key={contract.id} className="hover:shadow-md transition-all duration-200" data-testid="day-contract-card">
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-lg">
                          {contract.child_first_name} {contract.child_name}
                        </CardTitle>
                        <Badge variant="outline" className="text-xs">{contract.hourly_rate}€/h</Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {active ? (
                        <div className="flex items-center gap-4 p-3 rounded-lg bg-[var(--success)]/10 border border-[var(--success)]">
                          <div className="flex items-center gap-2 flex-1">
                            <div className="w-3 h-3 rounded-full bg-[var(--success)] animate-pulse" />
                            <span className="text-sm font-medium">
                              En cours depuis {active.start_time ? new Date(active.start_time).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '—'}
                            </span>
                          </div>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleStop(active.id)}
                            data-testid="stop-button"
                          >
                            <Square className="h-4 w-4 mr-1" /> Arrêter
                          </Button>
                        </div>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          <Button
                            onClick={() => handleStart(contract.id)}
                            style={{ backgroundColor: 'var(--primary-400)' }}
                            size="sm"
                            data-testid="start-button"
                          >
                            <Play className="h-4 w-4 mr-1" /> Commencer
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setSelectedContract(contract.id);
                              setAbsenceOpen(true);
                            }}
                            data-testid="absence-button"
                          >
                            <AlertTriangle className="h-4 w-4 mr-1" /> Absence
                          </Button>
                        </div>
                      )}

                      {/* Today's entries */}
                      {todayEntries.length > 0 && (
                        <div className="space-y-1 pt-2 border-t border-[var(--neutral-200)]">
                          <p className="text-xs font-medium text-[var(--text-secondary)]">Aujourd'hui</p>
                          {todayEntries.map(e => (
                            <div key={e.id} className="flex items-center justify-between text-sm py-1">
                              <span>{e.is_absent ? (
                                <Badge variant="outline" className="bg-[var(--warning)]/10 text-[var(--warning)] border-[var(--warning)] text-xs">
                                  {getAbsenceLabel(e.absence_type)}
                                </Badge>
                              ) : (
                                <>
                                  {e.start_time ? new Date(e.start_time).toLocaleTimeString('fr-FR', {hour:'2-digit',minute:'2-digit'}) : '—'}
                                  {' → '}
                                  {e.end_time ? new Date(e.end_time).toLocaleTimeString('fr-FR', {hour:'2-digit',minute:'2-digit'}) : 'En cours'}
                                </>
                              )}</span>
                              <span className="font-mono font-semibold text-[var(--primary-400)]">
                                {e.hours_worked ? `${e.hours_worked}h` : '—'}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </TabsContent>

            {/* ========== WEEK MODE ========== */}
            <TabsContent value="week" className="mt-4 space-y-4">
              <WeekView
                weekDates={weekDates}
                weekData={weekData}
                setWeekData={setWeekData}
                weekRef={weekRef}
                setWeekRef={setWeekRef}
                weekTotal={weekTotal}
                saving={saving}
                handleSaveWeek={handleSaveWeek}
                applyContractSchedule={applyContractSchedule}
                copyPreviousWeek={copyPreviousWeek}
                selectedContract={selectedContract}
                getContractName={getContractName}
                getSelectedContract={getSelectedContract}
                getHolidayName={getHolidayName}
                getTodayStr={getTodayStr}
                parseLocalDate={parseLocalDate}
                formatWeekLabel={formatWeekLabel}
              />
            </TabsContent>

            {/* ========== MONTH MODE ========== */}
            <TabsContent value="month" className="mt-4 space-y-4">
              <MonthView
                monthDates={monthDates}
                monthData={monthData}
                setMonthData={setMonthData}
                monthRef={monthRef}
                setMonthRef={setMonthRef}
                monthTotal={monthTotal}
                saving={saving}
                handleSaveMonth={handleSaveMonth}
                applyContractScheduleMonth={applyContractScheduleMonth}
                selectedContract={selectedContract}
                getContractName={getContractName}
                getSelectedContract={getSelectedContract}
                getHolidayName={getHolidayName}
                getTodayStr={getTodayStr}
                parseLocalDate={parseLocalDate}
              />
            </TabsContent>
          </Tabs>

          {/* History (below all modes) */}
          <EntryHistory
            entries={entries}
            selectedContract={selectedContract}
            contracts={contracts}
            monthRef={monthRef}
          />
        </>
      )}

      {/* Absence Dialog */}
      <AbsenceDialog
        isOpen={absenceOpen}
        onClose={() => setAbsenceOpen(false)}
        contractId={selectedContract}
        onAbsenceDeclared={fetchData}
      />
    </div>
  );
};

export default TimeTracking;
